import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import IdentifiersList, {
  AddIdentifierInFrame,
  type ReassignTarget,
} from 'app/common/components-next/identifiers'

import { AddIdentifierButton } from 'app/common/components-next/identifiers/add-identifier'
import Person from 'app/common/classes/person'
import { useRequiresBankcardPanNumber } from 'app/dataProviders/user/hooks'
import PlainButton from 'app/common/ui-next/plain-button'

import StudentItem from './StudentItem'
import useRequests from './useRequests'
import useIdentifiersPrepaidSettings from './useIdentifiersPrepaidSettings'
import useIdentifierPrepaidAppend from './useIdentifierPrepaidAppend'
import * as identifierTypes from 'app/common/constants/identifiers/types'
import { useError } from './useError'
import { useIsDataNotValid } from './useIsDataNotValid'
import { useIdentifierApprove } from './useIdentifierApprove'
import { createIdentifierApproveError } from './identifierApproveError'
import { getToken } from 'app/common/lib/token'
import { api } from 'app/api'

import styles from './Identifiers.scss'
import useIdentifiersSettings from './useIdentifiersPrepaidSettings'

type Props = {
  studentPerson: Person
  studentAccountID: string | null
  reassignEnabled: boolean
  reassignTargets: Array<ReassignTarget>
  timezone: string
}

const Identifiers = ({
  studentPerson,
  studentAccountID,
  reassignEnabled,
  reassignTargets,
  timezone,
}: Props) => {
  const { t } = useTranslation('identifiers-next')

  const [isDataNotValid, handleInvalidateData] = useIsDataNotValid()

  const {
    handleDataRequest,
    handleBlockIdentifier,
    handleUnblockIdentifier,
    handleReassignIdentifier,
    handleChangeIdentifierAttribute,
  } = useRequests()

  const [appended, setAppended] = useState({})

  const prepaidSettings = useIdentifiersPrepaidSettings(studentPerson.ID)
  const { enabled, types, format, useIdentifiersNewForm } = prepaidSettings || {}

  const { createErrorText, setError, resetError } = useError(studentPerson)

  const { append } = useIdentifierPrepaidAppend({
    student: studentPerson,
    onSuccess: setAppended,
    setError,
    resetError,
  })

  const setNotApprovedError = (identifierValue: string) => {
    setError(identifierTypes.IDENTIFIER_TINKOFF, identifierValue, 'PREPAID_NOT_FOUND')
  }

  const { requestRef, data: approveInfo } = useIdentifierApprove({
    params: { studentId: studentPerson.ID },
    autoload: false,
    autoupdate: false,
    error: createIdentifierApproveError(setNotApprovedError),
  })

  const requiresBankcardPanNumber = useRequiresBankcardPanNumber()

  useEffect(() => {
    setAppended({})
  }, [studentPerson.ID])

  const identifierInFrame = !!useIdentifiersNewForm
  const frameBaseUrl = new URL(api.baseUrl).origin

  const handleAddIdentifierModalChangeState = (prevStateOpened: boolean) => {
    if (prevStateOpened) {
      handleInvalidateData()
    }
  }

  return (
    <>
      <div className={styles.space} />

      {enabled && identifierInFrame && (
        <AddIdentifierInFrame
          onToggled={handleAddIdentifierModalChangeState}
          frameUrl={`${frameBaseUrl}/services/cards/form?app=REST_CABINET&cardholder_id=${studentPerson.id}&cardholder_type=Student&auth_token=${getToken()}`}
        >
          {(toggle) => (
            <PlainButton className={styles.addIdentifier} onClick={toggle}>
              {t('add_identifier_button', 'Добавить идентификатор')}
            </PlainButton>
          )}
        </AddIdentifierInFrame>
      )}
      {enabled && !identifierInFrame && (
        <AddIdentifierButton
          accountID={studentAccountID}
          bankCardUseFrame={requiresBankcardPanNumber}
          className={styles.addIdentifier}
          identifierFormat={format}
          availableIdentifierTypes={types}
          createErrorText={createErrorText}
          onSubmit={append}
          onRefresh={handleInvalidateData}
          onApprove={(params) => requestRef.current(params)}
          approveInfo={approveInfo}
        />
      )}

      <div className={styles.space} />
      <IdentifiersList
        personId={parseInt(studentPerson.ID, 10)}
        timezone={timezone}
        additional={appended}
        reassignEnabled={reassignEnabled}
        reassignTargets={reassignTargets}
        reassignItemView={StudentItem}
        onDataRequest={handleDataRequest}
        onBlockIdentifier={handleBlockIdentifier}
        onUnblockIdentifier={handleUnblockIdentifier}
        onReassignIdentitier={handleReassignIdentifier}
        onChangeIdentifierAttribute={handleChangeIdentifierAttribute}
        isDataNotValid={isDataNotValid}
        bankCardSpecial={requiresBankcardPanNumber}
      />
    </>
  )
}

export default Identifiers
